import { get, sortBy, isEmpty, flow } from 'lodash';
import { both, complement, equals, none, pipe, prop } from 'ramda';
import calculateDistance from 'haversine';
import {
  getScheduleWeekdaysFromSchedule,
  getScheduleWeekdaysOffFromSchedule,
  isWeekdayEnabled,
} from '../utils/schedule';
import { getStartOfDay } from '../utils/helpers';
import {
  LADY_CLIPPER_SHOP_ID,
  SQUIRE_FEE_SHOPS,
  NO_PROMO_BOX_SHOPS,
  KRWN_ZAZZ_PLACE_STE_FOY_SHOP_ID,
  CHOP_MURRAY_HILL_SHOP_ID,
} from '../constants';
import config from '../../config';

export const getShopById = (state, id) => state.shops.items.find(shop => shop.id === id);

export const getShopByRoute = (state, route) => state.shops.items.find(shop => shop.route === route);

const getShopProp = (state, route, property, fallback) => get(getShopByRoute(state, route), property, fallback);

export const getShopTimezone = (state, route) => getShopProp(state, route, 'timezone');

export const getShopBookNoPay = (state, route) => getShopProp(state, route, 'bookNoPay');

export const getShopAllowBookAndReserve = (state, route) => getShopProp(state, route, 'allowBookAndReserve');

export const getShopName = (state, route) => getShopProp(state, route, 'name');

export const getAdultsOnly = (state, route) => getShopProp(state, route, 'adultsOnly');

export const getImage = (state, route) => getShopProp(state, route, 'photos[0].url]');

export const getShopAllowBookWithAnyBarber = both(
  prop('allowBookWithAnyBarber'),
  pipe(prop('services'), complement(isEmpty))
);

export const getAllowBookWithAnyBarber = pipe(
  getShopByRoute,
  getShopAllowBookWithAnyBarber,
);

export const getAllowMultipleServices = (state, route) => getShopProp(state, route, 'allowMultipleServices');

export const getShopId = (state, route) => getShopProp(state, route, 'id');
export const getShopBrandId = (state, route) => getShopProp(state, route, 'brandId');

export const getShopRoute = (state, id) => {
  const shop = getShopById(state, id);
  if (!shop) return null;
  return shop.route;
};

const getDistance = (shop, position) => calculateDistance(
  { latitude: get(shop, 'location.coordinates[0]'),
    longitude: get(shop, 'location.coordinates[1]') },
  { latitude: get(position, 'lat'), longitude: get(position, 'lng') },
  { unit: 'mile' }
);

const getShop = (shop, position) => ({ ...shop, distance: getDistance(shop, position) });

export const getShops = (state) => {
  const position = state.position;
  const shops = state.shops.items.map(item => getShop(item, position));
  return sortBy(shops, 'distance');
};

export const areGcSalesDisabledForBarber = state => none(
    pipe(
      prop('barberSpecificGC'),
      equals(true),
    ),
  )(getShops(state));

export const getIsSquireFeeShop = (state, route) => {
  const shopId = getShopId(state, route);
  return SQUIRE_FEE_SHOPS.some(shop => shop === shopId);
};

export const getHasPromotionsEnabled = (state, route) =>
  !NO_PROMO_BOX_SHOPS.some(shop => shop === getShopId(state, route));

export const getHasShopLoyaltyProgram = (state, route) => {
  const reward = getShopProp(state, route, 'reward');
  return reward && reward.enabled;
};

export const getHasWaitingListEnabled = (state, route) => getShopProp(state, route, 'waiting_lists[0].enabled', false);

export const getIsLadyClipperShop = (state, route) => getShopProp(state, route, 'id') === LADY_CLIPPER_SHOP_ID;
export const getIsKrwnZazzPlaceSteFoyShop = (state, route) => (
  getShopProp(state, route, 'id') === KRWN_ZAZZ_PLACE_STE_FOY_SHOP_ID
);
export const getIsChopMurrayHillShop = (state, route) => (
  getShopProp(state, route, 'id') === CHOP_MURRAY_HILL_SHOP_ID
);
export const getIsPayFacShop = (state, route) => (
  config.payFacBrands.includes(getShopProp(state, route, 'brandId'))
);

export const getShopAdvanceBookDays = (state, route) => getShopProp(state, route, 'advanceBookDays', 7);
export const getShopCurrency = (state, route) => getShopProp(state, route, 'currency', '');
export const getShopAddress = (state, route) => getShopProp(state, route, 'address');
export const getShopCountry = (state, route) => getShopProp(state, route, 'country');

export const getShopZip = (state, route) => getShopProp(state, route, 'zip');

export const getShopLocation = (state, route) => getShopProp(state, route, 'location');

export const getShopSchedules = (state, route) => getShopProp(state, route, 'schedules');

const getShopScheduleWeekdays = (state, route) => flow(
  getShopSchedules,
  getScheduleWeekdaysFromSchedule,
)(state, route);

export const getShopDayEnabled = (state, route, activeDay) => flow(
  getShopScheduleWeekdays,
  weekdays => isWeekdayEnabled(getStartOfDay(activeDay), weekdays)
)(state, route);

export const getShopWeekdaysOff = (state, route) => flow(
  getShopSchedules,
  getScheduleWeekdaysOffFromSchedule,
)(state, route);

export const getShopLogo = (state, id) => flow(
  getShopById,
  item => get(item, 'logo.url'),
)(state, id);

export const getGiftCardEnabled = (state, route) => getShopProp(state, route, 'giftCardEnabled');

export const getNoExtrasForReserveNoPay = (state, route) => getShopProp(state, route, 'noExtrasForBookNoPay');



// WEBPACK FOOTER //
// ./src/app/shops/selectors.js